import {
    FORM_SUBMISSION_MEMORY_LEGACY_NAMESPACE_PREFIXES,
    FORM_SUBMISSION_MEMORY_NAMESPACE,
    FORM_SUBMISSION_MEMORY_STORAGE_FORMAT_VERSION,
} from "./constants";
import { buildFormSubmissionMemoryStorageKey } from "./buildFormSubmissionMemoryStorageKey";
import { isValidFormSubmissionMemoryValues } from "./formSubmissionMemoryHelpers";
import type {
    FormSubmissionMemoryEnvelope,
    FormSubmissionMemoryIdentity,
    FormSubmissionMemoryRuntimeContext,
} from "./types";

let legacyNamespacesCleanupDone = false;

function getLocalStorage(): Storage | null {
    if (typeof globalThis === "undefined") {
        return null;
    }

    try {
        const storage = globalThis.localStorage ?? globalThis.window?.localStorage;
        return storage ?? null;
    } catch {
        return null;
    }
}

/**
 * Одноразово удаляет известные legacy namespaces при bump storage format.
 */
export function cleanupLegacyFormSubmissionMemoryNamespaces(): void {
    if (legacyNamespacesCleanupDone) {
        return;
    }

    legacyNamespacesCleanupDone = true;
    const storage = getLocalStorage();
    if (storage == null) {
        return;
    }

    try {
        const keysToRemove: string[] = [];
        for (let index = 0; index < storage.length; index += 1) {
            const key = storage.key(index);
            if (key == null) {
                continue;
            }

            const isLegacy = FORM_SUBMISSION_MEMORY_LEGACY_NAMESPACE_PREFIXES.some(
                (prefix) => key === prefix || key.startsWith(`${prefix}:`),
            );
            if (isLegacy) {
                keysToRemove.push(key);
            }
        }

        for (const key of keysToRemove) {
            storage.removeItem(key);
        }
    } catch {
        // best-effort cleanup
    }
}

function parseEnvelope(raw: string | null): FormSubmissionMemoryEnvelope | null {
    if (raw == null || raw === "") {
        return null;
    }

    try {
        const parsed: unknown = JSON.parse(raw);
        if (parsed == null || typeof parsed !== "object" || Array.isArray(parsed)) {
            return null;
        }

        const envelope = parsed as Partial<FormSubmissionMemoryEnvelope>;
        if (
            envelope.storageFormatVersion !== FORM_SUBMISSION_MEMORY_STORAGE_FORMAT_VERSION ||
            typeof envelope.schemaVersion !== "number" ||
            typeof envelope.savedAt !== "string" ||
            envelope.values == null ||
            typeof envelope.values !== "object" ||
            Array.isArray(envelope.values)
        ) {
            return null;
        }

        return {
            storageFormatVersion: envelope.storageFormatVersion,
            schemaVersion: envelope.schemaVersion,
            savedAt: envelope.savedAt,
            values: envelope.values,
        };
    } catch {
        return null;
    }
}

/**
 * Читает remembered values из storage.
 *
 * @param identity identity записи
 * @param runtime runtime context
 * @param fields allowlist полей для shape validation
 * @returns partial values или `null`
 */
export function readFormSubmissionMemoryValues(
    identity: FormSubmissionMemoryIdentity,
    runtime: FormSubmissionMemoryRuntimeContext,
    fields: readonly string[] | undefined,
): Record<string, unknown> | null {
    const storageKey = buildFormSubmissionMemoryStorageKey(identity, runtime);
    const storage = getLocalStorage();
    if (storageKey == null || storage == null) {
        return null;
    }

    try {
        const envelope = parseEnvelope(storage.getItem(storageKey));
        if (envelope == null || envelope.schemaVersion !== identity.schemaVersion) {
            if (envelope != null) {
                storage.removeItem(storageKey);
            }
            return null;
        }

        if (!isValidFormSubmissionMemoryValues(envelope.values, fields)) {
            storage.removeItem(storageKey);
            return null;
        }

        return envelope.values;
    } catch {
        return null;
    }
}

/**
 * Записывает remembered values в storage (best-effort).
 *
 * @param identity identity записи
 * @param runtime runtime context
 * @param values values для persist
 */
export function writeFormSubmissionMemoryValues(
    identity: FormSubmissionMemoryIdentity,
    runtime: FormSubmissionMemoryRuntimeContext,
    values: Record<string, unknown>,
): void {
    const storageKey = buildFormSubmissionMemoryStorageKey(identity, runtime);
    const storage = getLocalStorage();
    if (storageKey == null || storage == null) {
        return;
    }

    const envelope: FormSubmissionMemoryEnvelope = {
        storageFormatVersion: FORM_SUBMISSION_MEMORY_STORAGE_FORMAT_VERSION,
        schemaVersion: identity.schemaVersion,
        savedAt: new Date().toISOString(),
        values,
    };

    try {
        storage.setItem(storageKey, JSON.stringify(envelope));
    } catch {
        // QuotaExceededError / SecurityError — не ломаем submit
    }
}

/**
 * Удаляет одну запись form submission memory.
 *
 * @param identity identity записи
 * @param runtime runtime context
 */
export function removeFormSubmissionMemoryRecord(
    identity: FormSubmissionMemoryIdentity,
    runtime: FormSubmissionMemoryRuntimeContext,
): void {
    const storageKey = buildFormSubmissionMemoryStorageKey(identity, runtime);
    const storage = getLocalStorage();
    if (storageKey == null || storage == null) {
        return;
    }

    try {
        storage.removeItem(storageKey);
    } catch {
        // ignore
    }
}

/**
 * Удаляет все записи текущего namespace form submission memory (best-effort).
 *
 * Не вызывается при logout/session loss: записи изолированы по server URL и user code
 * и должны переживать повторную авторизацию того же пользователя.
 *
 * TODO(form-submission-memory): для страницы управления сохранёнными данными добавить
 * перечисление записей и отдельную очистку записей текущего пользователя; эту функцию
 * оставить для явной глобальной очистки.
 */
export function removeAllFormSubmissionMemoryRecords(): void {
    const storage = getLocalStorage();
    if (storage == null) {
        return;
    }

    const prefix = `${FORM_SUBMISSION_MEMORY_NAMESPACE}:`;

    try {
        for (let index = storage.length - 1; index >= 0; index -= 1) {
            const key = storage.key(index);
            if (key != null && key.startsWith(prefix)) {
                storage.removeItem(key);
            }
        }
    } catch {
        // ignore
    }
}
